// @src/pre-deployment/src/services/ChapterImageStorageService.js v1.0
// - Firebase Storage service for chapter location images
// - Handles upload, download, delete operations
// - Generates proper storage paths following Firebase rules
// - Path pattern: languages/korean/assets/chapter-locations/level_{level}/chapter_{chapter}/{locationId}_{type}.{ext}

import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';

import { writeRawError } from '../components/ErrorHandling/AutomatedErrorSystem/RawErrorService';

import { storage } from './firebase.js';


/**
 * Dialogue type to module key mapping
 * When admin selects a dialogue type, store the full module key
 */
export const DIALOGUE_TYPE_MAP = {
  vocab: 'dialogueVocab',
  grammar: 'dialogueGrammar',
  comprehensive: 'dialogueComp',
  intro: 'dialogueIntro',
  conclusion: 'dialogueConclusion'
};

/**
 * Reverse mapping: module key to dialogue type
 */
export const MODULE_KEY_TO_DIALOGUE_TYPE = {
  dialogueVocab: 'vocab',
  dialogueGrammar: 'grammar',
  dialogueComp: 'comprehensive',
  dialogueIntro: 'intro',
  dialogueConclusion: 'conclusion'
};

/**
 * Available dialogue types for UI selection
 */
export const DIALOGUE_TYPES = [
  { value: 'vocab', label: 'Vocab Dialogue', moduleKey: 'dialogueVocab' },
  { value: 'grammar', label: 'Grammar Dialogue', moduleKey: 'dialogueGrammar' },
  { value: 'comprehensive', label: 'Comprehensive Dialogue', moduleKey: 'dialogueComp' },
  { value: 'intro', label: 'Intro Dialogue', moduleKey: 'dialogueIntro' },
  { value: 'conclusion', label: 'Conclusion Dialogue', moduleKey: 'dialogueConclusion' }
];

/**
 * ChapterImageStorageService
 * Handles Firebase Storage operations for chapter location images
 */
export const ChapterImageStorageService = {
  /**
   * Generate Firebase Storage path for chapter location image
   * Path: languages/korean/assets/chapter-locations/level_{level}/chapter_{chapter}/{locationId}_{type}.{ext}
   * 
   * @param {number} level - The level number
   * @param {number} chapter - The chapter number
   * @param {string} locationId - The location identifier (e.g., 'university', 'cafe')
   * @param {string} type - Image type: 'background' or 'thumbnail'
   * @param {string} filename - Original filename to extract extension
   * @returns {string} Firebase Storage path
   */
  getStoragePath(level, chapter, locationId, type, filename) {
    console.group('📁 ChapterImageStorageService: getStoragePath');
    console.log('Parameters:', { level, chapter, locationId, type, filename });

    // Extract file extension
    const ext = filename.split('.').pop().toLowerCase();

    // Sanitize locationId to only allow alphanumeric, underscores, and hyphens
    const sanitizedLocationId = locationId.replace(/[^a-zA-Z0-9_-]/g, '_');

    // Build path
    const storagePath = `languages/korean/assets/chapter-locations/level_${level}/chapter_${chapter}/${sanitizedLocationId}_${type}.${ext}`;

    console.log('Generated storage path:', storagePath);
    console.groupEnd();

    return storagePath;
  },

  /**
   * Upload image to Firebase Storage with progress tracking
   * 
   * @param {File} file - The file to upload
   * @param {number} level - The level number
   * @param {number} chapter - The chapter number
   * @param {string} locationId - The location identifier
   * @param {string} type - Image type: 'background' or 'thumbnail'
   * @param {function} onProgress - Callback for progress updates (0-100)
   * @returns {Promise<{success: boolean, downloadURL?: string, storagePath?: string, error?: string}>}
   */
  async uploadImage(file, level, chapter, locationId, type, onProgress = () => { }) {
    console.group('📤 ChapterImageStorageService: uploadImage');
    console.log('File:', file.name, 'Size:', file.size, 'Type:', file.type);
    console.log('Path params:', { level, chapter, locationId, type });

    // Validate storage is initialized
    if (!storage) {
      console.error('❌ Firebase Storage not initialized');
      console.groupEnd();
      return { success: false, error: 'Firebase Storage not initialized' };
    }

    // Validate file type
    const validTypes = ['image/webp', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      console.error('❌ Invalid file type:', file.type);
      console.groupEnd();
      return { success: false, error: `Invalid file type: ${file.type}. Allowed: webp, jpeg, png, gif` };
    }

    // Warn if file is large (> 10MB)
    if (file.size > 10 * 1024 * 1024) {
      console.warn('⚠️ File is larger than 10MB:', (file.size / (1024 * 1024)).toFixed(2), 'MB');
    }

    try {
      const storagePath = this.getStoragePath(level, chapter, locationId, type, file.name);
      console.log('Storage path:', storagePath);

      const storageRef = ref(storage, storagePath);
      const uploadTask = uploadBytesResumable(storageRef, file);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log(`Upload progress: ${progress.toFixed(1)}%`);
            onProgress(progress);
          },
          (error) => {
            console.error('❌ Upload error:', error.code, error.message);
            writeRawError(error, {
              componentName: 'ChapterImageStorageService',
              operation: 'uploadImage',
              level,
              chapter,
              locationId,
              type,
              severity: 'critical'
            });
            console.groupEnd();
            reject({ success: false, error: error.message });
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              console.log('✅ Upload complete. Download URL:', downloadURL);
              console.groupEnd();
              resolve({ success: true, downloadURL, storagePath });
            } catch (urlError) {
              console.error('❌ Failed to get download URL:', urlError);
              writeRawError(urlError, {
                componentName: 'ChapterImageStorageService',
                operation: 'uploadImage_getURL',
                storagePath,
                severity: 'high'
              });
              console.groupEnd();
              reject({ success: false, error: 'Failed to get download URL' });
            }
          }
        );
      });
    } catch (error) {
      console.error('❌ Upload exception:', error);
      writeRawError(error, {
        componentName: 'ChapterImageStorageService',
        operation: 'uploadImage',
        level,
        chapter,
        locationId,
        type,
        severity: 'critical'
      });
      console.groupEnd();
      return { success: false, error: error.message };
    }
  },

  /**
   * Delete image from Firebase Storage
   * 
   * @param {string} storagePath - The storage path of the image to delete
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async deleteImage(storagePath) {
    console.group('🗑️ ChapterImageStorageService: deleteImage');
    console.log('Storage path:', storagePath);

    if (!storage) {
      console.error('❌ Firebase Storage not initialized');
      console.groupEnd();
      return { success: false, error: 'Firebase Storage not initialized' };
    }

    if (!storagePath) {
      console.warn('⚠️ No storage path provided, skipping delete');
      console.groupEnd();
      return { success: true }; // Nothing to delete
    }

    try {
      const storageRef = ref(storage, storagePath);
      await deleteObject(storageRef);
      console.log('✅ Image deleted successfully');
      console.groupEnd();
      return { success: true };
    } catch (error) {
      if (error.code === 'storage/object-not-found') {
        console.log('ℹ️ Image not found (already deleted or never existed)');
        console.groupEnd();
        return { success: true }; // Not an error if already gone
      }
      console.error('❌ Delete error:', error.code, error.message);
      writeRawError(error, {
        componentName: 'ChapterImageStorageService',
        operation: 'deleteImage',
        storagePath,
        severity: 'critical'
      });
      console.groupEnd();
      return { success: false, error: error.message };
    }
  },

  /**
   * Extract storage path from Firebase download URL
   * Useful for deleting images when only the URL is stored
   * 
   * @param {string} downloadURL - The Firebase Storage download URL
   * @returns {string|null} The storage path, or null if extraction failed
   */
  extractStoragePath(downloadURL) {
    console.group('🔍 ChapterImageStorageService: extractStoragePath');
    console.log('Download URL:', downloadURL);

    if (!downloadURL) {
      console.log('⚠️ No URL provided');
      console.groupEnd();
      return null;
    }

    try {
      // Firebase Storage URLs have the format:
      // https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{encoded_path}?alt=media&token=...
      const match = downloadURL.match(/\/o\/([^?]+)/);
      if (match && match[1]) {
        const storagePath = decodeURIComponent(match[1]);
        console.log('Extracted storage path:', storagePath);
        console.groupEnd();
        return storagePath;
      }

      console.warn('⚠️ Could not extract path from URL');
      console.groupEnd();
      return null;
    } catch (error) {
      console.error('❌ Path extraction error:', error);
      console.groupEnd();
      return null;
    }
  },

  /**
   * Validate if a file is a valid image type
   * 
   * @param {File} file - The file to validate
   * @returns {{valid: boolean, error?: string}}
   */
  validateImageFile(file) {
    const validTypes = ['image/webp', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    const validExtensions = ['.webp', '.jpeg', '.jpg', '.png', '.gif'];

    if (!file) {
      return { valid: false, error: 'No file provided' };
    }

    // Check MIME type
    if (!validTypes.includes(file.type)) {
      return { valid: false, error: `Invalid file type: ${file.type}` };
    }

    // Check extension
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!validExtensions.includes(ext)) {
      return { valid: false, error: `Invalid file extension: ${ext}` };
    }

    return { valid: true };
  },

  /**
   * Get display name for dialogue module key
   * 
   * @param {string} moduleKey - The module key (e.g., 'dialogueVocab')
   * @returns {string} Display name (e.g., 'Dialogue (Vocab)')
   */
  getDialogueDisplayName(moduleKey) {
    if (moduleKey === 'dialogue') {
      return 'Dialogue';
    }

    const dialogueType = MODULE_KEY_TO_DIALOGUE_TYPE[moduleKey];
    if (dialogueType) {
      const typeInfo = DIALOGUE_TYPES.find(t => t.value === dialogueType);
      return typeInfo ? `Dialogue (${typeInfo.label.replace(' Dialogue', '')})` : moduleKey;
    }

    return moduleKey;
  },

  /**
   * Check if a module key is a dialogue variant
   * 
   * @param {string} moduleKey - The module key to check
   * @returns {boolean}
   */
  isDialogueModule(moduleKey) {
    return moduleKey === 'dialogue' || moduleKey in MODULE_KEY_TO_DIALOGUE_TYPE;
  }
};

export default ChapterImageStorageService;

